import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import "@/App.css";

// Lazy-load all authenticated/secondary pages
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const RoleSelection = lazy(() => import("@/pages/RoleSelection"));
const BusinessDashboard = lazy(() => import("@/pages/BusinessDashboard"));
const WorkerDashboard = lazy(() => import("@/pages/WorkerDashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const MessagingPage = lazy(() => import("@/pages/MessagingPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const BusinessDirectory = lazy(() => import("@/pages/BusinessDirectory"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const ForWorkersPage = lazy(() => import("@/pages/ForWorkersPage"));
const ForBusinessesPage = lazy(() => import("@/pages/ForBusinessesPage"));
const SupportChat = lazy(() => import("@/components/SupportChat"));

function DashboardRedirect() {
  const { user } = useAuth();
  const location = useLocation();
  const suffix = location.search + location.hash;
  if (!user?.role) return <Navigate to="/role-selection" replace />;
  if (user.role === "business") return <Navigate to={`/business${suffix}`} replace />;
  if (user.role === "worker") return <Navigate to={`/worker${suffix}`} replace />;
  if (user.role === "admin") return <Navigate to={`/admin${suffix}`} replace />;
  return <Navigate to="/" replace />;
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading, checkAuth } = useAuth();
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!loading && !user && !checked && !checking) {
      setChecking(true);
      checkAuth().finally(() => { setChecking(false); setChecked(true); });
    }
  }, [loading, user, checked, checking, checkAuth]);

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="loading-spinner">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-3 text-sm text-muted-foreground font-body">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <Suspense fallback={null}><AuthCallback /></Suspense>;
  }
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="w-8 h-8 border-2 border-[#00BCD4] border-t-transparent rounded-full animate-spin" /></div>}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/for-workers" element={<ForWorkersPage />} />
        <Route path="/for-businesses" element={<ForBusinessesPage />} />
        <Route path="/directory" element={<BusinessDirectory />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/role-selection" element={<ProtectedRoute><RoleSelection /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardRedirect /></ProtectedRoute>} />
        <Route path="/business" element={<ProtectedRoute allowedRoles={["business"]}><BusinessDashboard /></ProtectedRoute>} />
        <Route path="/worker" element={<ProtectedRoute allowedRoles={["worker"]}><WorkerDashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute><MessagingPage /></ProtectedRoute>} />
        <Route path="/messages/:conversationId" element={<ProtectedRoute><MessagingPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
        <BrowserRouter>
          <AuthProvider>
            <Toaster position="top-right" richColors />
            <AppRouter />
            <Suspense fallback={null}><SupportChat /></Suspense>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
